import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError } from "../lib/api";
import { useOrdersSocket } from "../hooks/useOrdersSocket";
import { useAuth } from "../context/AuthContext";
import { STATUS_LABELS, STATUS_STYLES_DARK, WA_STATUS_LABELS, orderItems, isOverdue } from "../lib/constants";
import { toast } from "sonner";
import { Wifi, WifiOff, Package, Truck, Clock, CheckCircle2, X, AlertTriangle, Mail, Undo2, LogOut, RotateCw, Volume2, VolumeX, Monitor, Bell, Flame, Maximize, Minimize, Calendar, Columns3 } from "lucide-react";
import { DeliverModal } from "../components/DeliverModal";

function elapsed(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "az önce";
  if (mins < 60) return `${mins} dk`;
  const h = Math.floor(mins / 60);
  return `${h} sa ${mins % 60} dk`;
}

function isUrgent(order) {
  if (!["new", "preparing"].includes(order.status)) return false;
  if (!order.delivery_date) return false;
  const d = new Date(`${order.delivery_date}T23:59:59`);
  if (isNaN(d.getTime())) return false;
  return d.getTime() - Date.now() <= 24 * 3600 * 1000;
}

// Hedef kart genişliği (px). Ekran genişliğine göre 2–5 arası sütun hesaplanır.
// 32" FHD TV (~1920px) uzaktan okunacağı için 2 geniş/büyük sütun gösterilir;
// 2560px'de 3, 3840px (4K büyük TV)'de 4-5 sütun olur.
const TARGET_CARD_PX = 840;

export default function Kiosk() {
  const [orders, setOrders] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [deliverOrder, setDeliverOrder] = useState(null);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [tvMode, setTvMode] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [flash, setFlash] = useState(null);
  const [isFs, setIsFs] = useState(false);
  const [autoFs, setAutoFs] = useState(() => {
    try { return localStorage.getItem("kiosk_auto_fs") === "1"; } catch { return false; }
  });
  const [showFsPrompt, setShowFsPrompt] = useState(false);
  const [cols, setCols] = useState(() => {
    try {
      const v = localStorage.getItem("kiosk_cols");
      if (!v || v === "auto") return "auto";
      const n = Number(v);
      return n >= 2 && n <= 5 ? n : "auto";
    } catch { return "auto"; }
  });
  const [winW, setWinW] = useState(typeof window !== "undefined" ? window.innerWidth : 1920);
  const audioRef = useRef(null);
  const soundOnRef = useRef(true);
  soundOnRef.current = soundOn;
  const { user, logout } = useAuth();

  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const playBeep = useCallback(() => {
    if (!soundOnRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!audioRef.current) audioRef.current = new Ctx();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      [0, 0.18, 0.36].forEach((t) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, now + t);
        gain.gain.exponentialRampToValueAtTime(0.3, now + t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.15);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + t);
        osc.stop(now + t + 0.16);
      });
    } catch {}
  }, []);

  const toggleFullscreen = useCallback(() => {
    try {
      if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
      else document.exitFullscreen?.();
    } catch {}
  }, []);

  const toggleAutoFs = useCallback(() => {
    setAutoFs((v) => {
      const nv = !v;
      try { localStorage.setItem("kiosk_auto_fs", nv ? "1" : "0"); } catch {}
      return nv;
    });
  }, []);

  useEffect(() => {
    const onFs = () => {
      const fs = !!document.fullscreenElement;
      setIsFs(fs);
      if (fs) setShowFsPrompt(false);
    };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  useEffect(() => {
    if (autoFs && !document.fullscreenElement) {
      const p = document.documentElement.requestFullscreen?.();
      if (p && typeof p.catch === "function") p.catch(() => setShowFsPrompt(true));
      else if (!document.fullscreenElement) setShowFsPrompt(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let sentinel = null;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator && document.visibilityState === "visible") {
          sentinel = await navigator.wakeLock.request("screen");
        }
      } catch {}
    };
    const onVis = () => { if (document.visibilityState === "visible") acquire(); };
    acquire();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      try { sentinel && sentinel.release(); } catch {}
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Live fallback: poll every 8s so the board stays current even if the
  // WebSocket is blocked or (in production) served by a different replica.
  useEffect(() => {
    const t = setInterval(() => load(), 8000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => setOrders((o) => [...o]), 30000); // refresh elapsed timers
    return () => clearInterval(t);
  }, []);

  const onWs = useCallback((msg) => {
    if (msg.type === "reconnected") { load(); return; }
    if (msg.type === "order_deleted") {
      setOrders((prev) => prev.filter((o) => o.id !== msg.order_id));
      return;
    }
    if (msg.type === "order_created") {
      playBeep();
      const f = { order_no: msg.order.order_no, company: msg.order.customer_snapshot?.company_name, ts: Date.now() };
      setFlash(f);
      setTimeout(() => setFlash((cur) => (cur && cur.ts === f.ts ? null : cur)), 6000);
      setOrders((prev) => (prev.some((o) => o.id === msg.order.id) ? prev : [msg.order, ...prev]));
    }
    if (msg.type === "order_updated") {
      setOrders((prev) => {
        const idx = prev.findIndex((o) => o.id === msg.order.id);
        if (idx === -1) return [msg.order, ...prev];
        const copy = [...prev]; copy[idx] = msg.order; return copy;
      });
    }
  }, [load, playBeep]);

  const { connected } = useOrdersSocket(onWs);

  const doMarkReady = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${confirm.id}/mark-ready`);
      if (data.whatsapp_status === "failed") {
        toast.error(`Hazır işaretlendi ancak e-posta gönderilemedi. Personele uyarı iletildi.`);
      } else if (data.whatsapp_status === "success") {
        toast.success(`#${data.order_no} HAZIR · e-posta gönderildi ✓`);
      } else {
        toast.success(`#${data.order_no} HAZIR olarak işaretlendi`);
      }
      setConfirm(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doUndo = async (o) => {
    try {
      await api.post(`/orders/${o.id}/undo-ready`);
      toast.success(`#${o.order_no} geri alındı`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const doResend = async (o) => {
    try {
      const { data } = await api.post(`/orders/${o.id}/resend-whatsapp`);
      if (data.whatsapp_status === "success") toast.success(`#${o.order_no} e-posta gönderildi ✓`);
      else toast.error(`#${o.order_no} tekrar başarısız — e-posta uyarısı üretildi`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const setStatus = async (o, status) => {
    try { await api.patch(`/orders/${o.id}/status`, { status }); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sortByCreated = (a, b) => new Date(a.created_at) - new Date(b.created_at);
  const pending = orders.filter((o) => ["new", "preparing"].includes(o.status)).sort(sortByCreated);
  const completed = orders.filter((o) => ["ready", "notified", "delivered"].includes(o.status)).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  const all = [...orders].sort(sortByCreated);
  const visible = tab === "pending" ? pending : tab === "completed" ? completed : all;

  const autoCols = Math.max(2, Math.min(5, Math.floor(winW / TARGET_CARD_PX)));
  const effectiveCols = cols === "auto" ? autoCols : cols;
  const fontScale = Math.max(0.72, Math.min(1.7, 1 + (4 - effectiveCols) * 0.18));
  const applyCols = (v) => { setCols(v); try { localStorage.setItem("kiosk_cols", String(v)); } catch {} };

  useEffect(() => {
    if (!tvMode) return;
    const id = setInterval(() => {
      const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 4;
      if (atBottom) window.scrollTo({ top: 0, behavior: "smooth" });
      else window.scrollBy({ top: 2, behavior: "auto" });
    }, 60);
    return () => clearInterval(id);
  }, [tvMode, visible.length]);

  return (
    <div className="kiosk-bg min-h-screen text-white">
      {flash && (
        <div data-testid="kiosk-new-order-flash" className="fixed inset-x-0 top-0 z-[70] flex items-center justify-center gap-2 bg-emerald-500 py-3 text-lg font-black uppercase tracking-wider text-white shadow-lg animate-pulse">
          <Bell className="h-5 w-5" /> YENİ SİPARİŞ: #{flash.order_no} — {flash.company}
        </div>
      )}
      {showFsPrompt && (
        <div data-testid="kiosk-fs-prompt" className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-6 bg-[#090D16]/95 p-6 text-center backdrop-blur">
          <Maximize className="h-16 w-16 text-blue-400" />
          <p className="font-display text-2xl font-bold text-white">Pano tam ekran modunda daha iyi görünür</p>
          <button data-testid="kiosk-fs-prompt-btn" onClick={() => { toggleFullscreen(); setShowFsPrompt(false); }} className="rounded-2xl bg-blue-500 px-8 py-4 text-xl font-black uppercase tracking-wide text-white shadow-lg shadow-blue-500/30 hover:bg-blue-400">
            Tam Ekrana Geç
          </button>
          <button data-testid="kiosk-fs-prompt-dismiss" onClick={() => setShowFsPrompt(false)} className="text-sm text-slate-400 hover:text-white">Şimdi değil</button>
        </div>
      )}
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-[#090D16]/95 px-5 py-4 backdrop-blur">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-[1.4rem] font-black tracking-tight md:text-[1.75rem]">ÜRETİM PANOSU</h1>
            <p className="text-[10px] text-slate-400">Tek Kuyruk · {visible.length} sipariş · {user?.full_name}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <span
              data-testid="ws-connection-status-pill"
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-bold uppercase tracking-wider ${
                connected ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300 pulse-glow" : "border-rose-500/50 bg-rose-500/15 text-rose-300"
              }`}
            >
              {connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {connected ? "CANLI BAĞLANTI" : "BAĞLANTI KOPUK"}
            </span>
            <div data-testid="kiosk-cols-control" className="hidden items-center gap-0.5 rounded-lg border border-slate-700 px-1 py-0.5 md:flex" title="Yan yana sütun sayısı (OTO = ekrana göre otomatik)">
              <Columns3 className="mx-0.5 h-3 w-3 text-slate-400" />
              <button
                data-testid="kiosk-cols-auto"
                onClick={() => applyCols("auto")}
                className={`h-6 rounded px-1.5 text-[10px] font-black uppercase ${cols === "auto" ? "bg-blue-500 text-white" : "text-slate-400 hover:bg-slate-800"}`}
                title="Ekran genişliğine göre otomatik"
              >
                OTO
              </button>
              <button
                data-testid="kiosk-cols-minus"
                onClick={() => applyCols(Math.max(2, effectiveCols - 1))}
                disabled={effectiveCols <= 2}
                className="flex h-6 w-6 items-center justify-center rounded text-sm font-black text-slate-300 hover:bg-slate-800 disabled:opacity-30"
              >
                −
              </button>
              <span data-testid="kiosk-cols-value" className="min-w-[2ch] text-center text-xs font-black text-white">{effectiveCols}</span>
              <button
                data-testid="kiosk-cols-plus"
                onClick={() => applyCols(Math.min(5, effectiveCols + 1))}
                disabled={effectiveCols >= 5}
                className="flex h-6 w-6 items-center justify-center rounded text-sm font-black text-slate-300 hover:bg-slate-800 disabled:opacity-30"
              >
                +
              </button>
            </div>
            <button data-testid="kiosk-fullscreen-toggle" onClick={toggleFullscreen} className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800" title="Tam ekran">
              {isFs ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            </button>
            <button data-testid="kiosk-sound-toggle" onClick={() => { setSoundOn((s) => !s); try { playBeep(); } catch {} }} className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800" title="Sesli uyarı">
              {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4 text-slate-500" />}
            </button>
            <button data-testid="kiosk-tv-toggle" onClick={() => setTvMode((v) => !v)} className={`flex items-center gap-1 rounded-lg border px-1.5 py-1 text-[11px] font-bold uppercase tracking-wide ${tvMode ? "border-blue-500 bg-blue-500/20 text-white" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`} title="TV modu (otomatik kaydırma)">
              <Monitor className="h-4 w-4" /> TV
            </button>
            <button data-testid="kiosk-logout" onClick={logout} className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-700 text-slate-400 hover:bg-slate-800">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex gap-2 overflow-x-auto border-b border-slate-800 bg-[#090D16]/95 px-5 py-3">
        {[
          { id: "all", label: "Tüm Siparişler", count: all.length },
          { id: "pending", label: "Bekleyen Siparişler", count: pending.length },
          { id: "completed", label: "Tamamlananlar", count: completed.length },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`kiosk-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-bold uppercase tracking-wide transition-colors ${
              tab === t.id ? "border-blue-500 bg-blue-500/20 text-white" : "border-slate-700 text-slate-400 hover:bg-slate-800"
            }`}
          >
            {t.label}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${tab === t.id ? "bg-blue-500 text-white" : "bg-slate-800 text-slate-300"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      <div className="px-5 pb-5 pt-3">
        {visible.length === 0 && (
          <div className="flex min-h-[50vh] flex-col items-center justify-center text-slate-500">
            <Package className="mb-4 h-16 w-16" />
            <p className="font-display text-2xl font-bold">Bu bölümde sipariş yok</p>
            <p className="text-sm">Yeni siparişler buraya anlık düşecek.</p>
          </div>
        )}

        <div className="grid items-start gap-4" style={{ gridTemplateColumns: `repeat(${effectiveCols}, minmax(0, 1fr))` }}>
          {visible.map((o) => {
            const isReady = o.status === "ready";
            const isNotified = o.status === "notified";
            const urgent = isUrgent(o);
            return (
              <div
                key={o.id}
                data-testid={`kiosk-card-${o.order_no}`}
                style={{ fontSize: `${fontScale}rem` }}
                className={`flex flex-col rounded-3xl border bg-[#111827] px-6 py-3 shadow-xl transition-all ${
                  urgent
                    ? "border-l-[10px] border-rose-500 shadow-[0_0_28px_rgba(244,63,94,0.35)] ring-1 ring-rose-500/40"
                    : isReady
                    ? "border-l-[10px] border-emerald-500 shadow-[0_0_28px_rgba(16,185,129,0.25)]"
                    : isNotified
                    ? "border-l-[10px] border-cyan-500"
                    : o.status === "preparing"
                    ? "border-l-[10px] border-amber-500"
                    : "border-l-[10px] border-blue-500"
                } border-slate-800`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono font-bold text-slate-400" style={{ fontSize: "0.43em" }}>{o.order_no}</div>
                    <div className="font-display font-black leading-tight text-white break-words" style={{ fontSize: "0.99em" }}>{o.customer_snapshot?.company_name}</div>
                    <div className="text-slate-400" style={{ fontSize: "0.43em" }}>{orderItems(o).length} kalem ürün</div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1 max-w-[48%]">
                    {urgent && (
                      <span data-testid={`kiosk-urgent-${o.order_no}`} className="inline-flex items-center gap-0.5 rounded-full bg-rose-500 px-1.5 py-0.5 text-[0.5rem] font-black uppercase tracking-wider text-white pulse-glow">
                        <Flame className="h-2 w-2" /> ACİL
                      </span>
                    )}
                    {isOverdue(o) && (
                      <span data-testid={`kiosk-overdue-${o.order_no}`} className="inline-flex items-center gap-0.5 rounded-full bg-rose-600 px-1.5 py-0.5 text-[0.5rem] font-black uppercase tracking-wider text-white pulse-glow">
                        <Clock className="h-2 w-2" /> GECİKTİ
                      </span>
                    )}
                    <span className={`rounded-full border px-1.5 py-1 text-right font-bold uppercase leading-tight break-words ${STATUS_STYLES_DARK[o.status]}`} style={{ fontSize: "0.4em" }}>
                      {STATUS_LABELS[o.status]}
                    </span>
                  </div>
                </div>

                <div className="mt-1 space-y-1">
                  {orderItems(o).map((it, i) => (
                    <div key={i} className="flex items-center justify-between rounded-2xl bg-slate-800/60 p-1.5">
                      <div className="min-w-0">
                        <div className="font-mono font-black text-white truncate" style={{ fontSize: "0.87em" }}>{it.product_code}</div>
                        <div className="truncate text-slate-300" style={{ fontSize: "0.56em" }}>{it.product_name}</div>
                      </div>
                      <div className="shrink-0 pl-2 text-right font-mono font-extrabold text-emerald-300" style={{ fontSize: "1.08em" }}>
                        {it.quantity}<span className="ml-1 text-slate-400" style={{ fontSize: "0.55em" }}>{it.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-400" style={{ fontSize: "0.42em" }}>
                  {o.created_at && <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{new Date(o.created_at).toLocaleDateString("tr-TR")}</span>}
                  {o.shipping_type && <span className="flex items-center gap-1.5"><Truck className="h-4 w-4" />{o.shipping_type}</span>}
                  <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{elapsed(o.created_at)} önce</span>
                  <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" />{WA_STATUS_LABELS[o.whatsapp_status]}</span>
                </div>

                {o.whatsapp_status === "failed" && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1 rounded-lg border border-rose-500/40 bg-rose-500/10 px-1 py-0.5 text-[0.525rem] text-rose-300">
                    <span className="flex items-center gap-1"><AlertTriangle className="h-[0.35rem] w-[0.35rem]" /> E-posta gönderilemedi — personele uyarı iletildi.</span>
                    <button
                      data-testid={`kiosk-resend-${o.order_no}`}
                      onClick={() => doResend(o)}
                      className="ml-auto flex items-center gap-1 rounded bg-rose-500 px-1 py-0.5 text-[0.525rem] font-bold text-white hover:bg-rose-400"
                    >
                      <RotateCw className="h-[0.35rem] w-[0.35rem]" /> Yeniden Gönder
                    </button>
                  </div>
                )}

                {o.note && <div className="mt-2 rounded-lg bg-slate-800/40 px-2 py-1.5 text-[0.525rem] text-slate-300">Not: {o.note}</div>}

                <div className="flex flex-wrap gap-3 pt-2">
                  {(o.status === "new" || o.status === "preparing") && (
                    <button
                      data-testid={`kiosk-ready-${o.order_no}`}
                      onClick={() => setConfirm(o)}
                      className="flex-1 rounded-xl bg-emerald-500 py-2.5 font-black uppercase tracking-wide text-white shadow-lg shadow-emerald-500/30 transition-all hover:bg-emerald-400"
                      style={{ fontSize: "0.63em" }}
                    >
                      ✓ HAZIR
                    </button>
                  )}
                  {isReady && (
                    <button data-testid={`kiosk-undo-${o.order_no}`} onClick={() => doUndo(o)} className="flex items-center justify-center gap-2 rounded-2xl border border-slate-600 bg-slate-800 px-5 py-4 font-bold text-slate-200 hover:bg-slate-700" style={{ fontSize: "0.95em" }}>
                      <Undo2 className="h-5 w-5" /> Geri Al
                    </button>
                  )}
                  {(isReady || isNotified) && (
                    <button data-testid={`kiosk-deliver-${o.order_no}`} onClick={() => setDeliverOrder(o)} className="flex-1 rounded-2xl border border-cyan-500/50 bg-cyan-500/10 py-4 font-bold text-cyan-300 hover:bg-cyan-500/20" style={{ fontSize: "1.05em" }}>
                      Teslim Edildi
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="ready-confirm-modal">
          <div className="w-full max-w-lg rounded-3xl border border-emerald-500/40 bg-[#111827] p-6 shadow-2xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/20 text-emerald-400">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-black text-white">Siparişi Hazır İşaretle</h2>
                <p className="text-sm text-slate-400">ve müşteriye e-posta bildirimi gönder</p>
              </div>
            </div>

            <div className="mb-4 rounded-2xl bg-slate-800/60 p-4">
              <div className="font-mono text-sm text-slate-400">{confirm.order_no} — {confirm.customer_snapshot?.company_name}</div>
              <div className="mt-1 space-y-1">
                {orderItems(confirm).map((it, i) => (
                  <div key={i} className="text-white">
                    <span className="font-mono font-bold">{it.product_code}</span> {it.product_name} ·{" "}
                    <span className="font-semibold">{it.quantity} {it.unit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-emerald-400">
                <Mail className="h-4 w-4" /> Gönderilecek E-posta Önizleme
              </div>
              <p className="text-sm leading-relaxed text-slate-200">
                Merhaba {confirm.customer_snapshot?.company_name}, #{confirm.order_no} numaralı siparişiniz üretilip paketlenmiş ve HAZIR duruma getirilmiştir. Ürünler: {orderItems(confirm).map((it) => `${it.product_name} (${it.quantity} ${it.unit})`).join(", ")}. Sevk Şekli: {confirm.shipping_type || "-"}.
              </p>
              <div className="mt-2 text-xs text-slate-500">Hedef: {confirm.customer_snapshot?.email || "E-posta kayıtlı değil — personele uyarı iletilir"}</div>
            </div>

            <div className="flex gap-3">
              <button data-testid="ready-cancel-btn" onClick={() => setConfirm(null)} disabled={busy} className="flex items-center justify-center gap-2 rounded-2xl border border-slate-600 bg-slate-800 px-5 py-4 font-bold text-slate-200 hover:bg-slate-700">
                <X className="h-5 w-5" /> Vazgeç
              </button>
              <button data-testid="order-ready-confirm-btn" onClick={doMarkReady} disabled={busy} className="flex-1 rounded-2xl bg-emerald-500 py-4 text-lg font-black uppercase tracking-wide text-white shadow-lg shadow-emerald-500/30 hover:bg-emerald-400 disabled:opacity-60">
                {busy ? "Gönderiliyor..." : "Onayla & Bildirim Gönder"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deliverOrder && <DeliverModal order={deliverOrder} dark onClose={() => setDeliverOrder(null)} onDone={() => setDeliverOrder(null)} />}
    </div>
  );
}
